import type { FC } from 'react';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import Avatar from '@atlaskit/avatar';
import type { SizeType as AvatarSizeType } from '@atlaskit/avatar/types';
import { Box, Inline, Text } from '@atlaskit/primitives';
import Link from '@atlaskit/link';

import { ProfileCardWrapper } from '@confluence/profile';
import type { ContentStatus } from '@confluence/page-utils/entry-points/enums';
import { useSessionData } from '@confluence/session-data';
import { PEOPLE_DIRECTORY } from '@confluence/named-routes';

import {
	ContentOwnershipContextProvider,
	ContentOwnershipContextConsumer,
} from '../ContentOwnershipContext';
import type { ContentOwnershipContextType } from '../ContentOwnershipContext';

import { checkIsChangeOwnerAvailable } from './checkIsChangeOwnerAvailable';
import { ChangeContentOwnerButton } from './ChangeContentOwnerButton';

const i18n = defineMessages({
	owner: {
		id: 'content-ownership.content-owner-item.owner',
		defaultMessage: 'Owner',
		description: 'A label distinguishing the user as the owner of a page',
	},
	creator: {
		id: 'content-ownership.content-owner-item.creator',
		defaultMessage: 'Creator',
		description: 'A label distinguishing the user as the person who created the blogpost',
	},
	changeOwner: {
		id: 'content-ownership.content-owner-item.change-owner',
		defaultMessage: 'Change owner',
		description: 'An icon label within a button used to change the owner of a page',
	},
	accessibleAvatarLabel: {
		id: 'content-ownership.content-owner-item.accessible-avatar-label',
		defaultMessage: '{displayName}’s profile picture',
		description: "Alt text and aria-label for a user's profile picture",
	},
});

type ContentOwnerItemWithChangeOwnerProps = {
	ownerAccountId?: string | null;
	ownerName?: string | null;
	ownerAvatarUrl?: string | null;
	contentType?: string | null;
	contentStatus?: ContentStatus | string | null;
	isCurrentUserSpaceAdmin: boolean;
	avatarSize?: AvatarSizeType;
	// use this if you need to render the change owner modal from a higher level in the tree. example: the button is in a dropdown that closes when the modal opens.
	overrideChangeOwnerClick?: () => void;
};

export const ContentOwnerItemWithChangeOwner: FC<ContentOwnerItemWithChangeOwnerProps> = ({
	ownerAccountId,
	ownerName,
	ownerAvatarUrl,
	contentType,
	contentStatus,
	isCurrentUserSpaceAdmin,
	avatarSize = 'large',
	overrideChangeOwnerClick,
}) => {
	const { formatMessage } = useIntl();
	const { userId: currentUserId } = useSessionData();

	// The concept of "ownership" doesn't apply to blogposts for some reason, so blogposts should refer to "creator"
	const ownerOrCreatorString =
		contentType === 'blogpost' ? formatMessage(i18n.creator) : formatMessage(i18n.owner);
	const ownerProfileUrl = ownerAccountId ? `${PEOPLE_DIRECTORY.toUrl()}/${ownerAccountId}` : '';
	const isChangeOwnerAvailable = checkIsChangeOwnerAvailable({
		contentStatus,
		contentType,
		isContentOwner: ownerAccountId === currentUserId,
		isSpaceAdmin: isCurrentUserSpaceAdmin,
	});

	const renderChangeOwnerButton = () => {
		if (overrideChangeOwnerClick) {
			return <ChangeContentOwnerButton onClick={overrideChangeOwnerClick} />;
		}

		return (
			<ContentOwnershipContextProvider>
				<ContentOwnershipContextConsumer>
					{({ openModal }: ContentOwnershipContextType) => (
						<ChangeContentOwnerButton onClick={openModal} />
					)}
				</ContentOwnershipContextConsumer>
			</ContentOwnershipContextProvider>
		);
	};

	return (
		<Inline spread="space-between" alignBlock="center" space="space.100">
			<Inline alignBlock="center" space="space.100">
				<ProfileCardWrapper userId={ownerAccountId}>
					<Avatar
						size={avatarSize}
						borderColor="transparent"
						href={ownerProfileUrl}
						src={ownerAvatarUrl ?? undefined}
						name={formatMessage(i18n.accessibleAvatarLabel, {
							displayName: ownerName,
						})}
					/>
				</ProfileCardWrapper>
				<Box>
					<Box>
						{/* To be replaced by Link introduced in https://stash.atlassian.com/projects/ATLASSIAN/repos/atlassian-frontend-monorepo/pull-requests/163767/overview */}
						<Link appearance="subtle" href={ownerProfileUrl}>
							{ownerName}
						</Link>
					</Box>
					<Inline>
						<Text size="small" color="color.text.subtlest">
							{ownerOrCreatorString}
						</Text>
					</Inline>
				</Box>
			</Inline>
			{isChangeOwnerAvailable && renderChangeOwnerButton()}
		</Inline>
	);
};
